import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_groupMutation,
  useGET_groupByIdQuery,
  usePOST_groupMutation,
  usePUT_groupMutation,
} from 'redux/api/groupsApi'
import { useGET_teacherProfileSelectQuery } from 'redux/api/teachersApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import SelectField from 'components/atoms/fields/SelectField'
import InputField from 'components/atoms/fields/InputField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/groupModel'
import { crudColorMap, crudIconMap, gradeSelectData, academicYearSelectData } from 'utils/helpers'

import { ActionType, IGroupData } from 'types'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
  tabText?: string
  tabKey?: string
}

const GroupForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const backToSlug = '/groups'
  const editSlug = `/groups/edit/${id}`
  const editPrefix = '/groups/edit/'

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    editPrefix,
    entityName: `${t('common.groups')}`,
    entityId: id,
    getEntityQuery: useGET_groupByIdQuery,
    createEntityMutation: usePOST_groupMutation,
    editEntityMutation: usePUT_groupMutation,
    deleteEntityMutation: useDELETE_groupMutation,
  })

  const { mentor, grade_level, academic_year, sign } = formModel()

  const serverValues = entity ? {
    ...entity,
    grade_level: entity.grade_level_option,
    academic_year: entity.academic_year
  } : null

  return (
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: IGroupData = {
          mentor_id: values.mentor.id,
          grade_level: values.grade_level.id,
          academic_year: values.academic_year.id,
          sign: values.sign,
        }
        handleSubmit(action, id, data)
      }}
    >
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>

         <Grid item xs={12} lg={3}>
            <SelectField
              fieldProps={{ ...mentor }}
              apiCall={useGET_teacherProfileSelectQuery}
              disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <SelectField
              fieldProps={{ ...grade_level }}
              apiCall={gradeSelectData}
              disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <SelectField
              fieldProps={{ ...academic_year }}
              apiCall={academicYearSelectData}
              disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <InputField fieldProps={{ ...sign }} disabled={viewOnly} />
          </Grid>



          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  )
}
export default GroupForm
